import { useEffect, useState } from 'react';
import { ApiFailure, api } from '../lib/api';
import type { OrderProof } from '../lib/types';
import { MapPin } from 'lucide-react';
import { InlineIcon } from './InlineIcon';

/**
 * 凭证照片。
 *
 * ★ 桶是私有的,图片地址要现换预签名 URL —— POD 照片里有门牌号和收件人信息,
 *   公开直链一旦泄露就是持久的隐私暴露(见 uploads.controller.ts)。
 *   代价是每张图多一次请求,可以接受:详情页不是高频页面。
 */
export function ProofGallery({
  proofs,
  labels,
}: {
  proofs: OrderProof[];
  labels: Record<string, string>;
}) {
  const [zoom, setZoom] = useState<string | null>(null);

  // 按类型分组:取货照和签收照是两回事,混在一起看不出哪张证明哪件事
  const groups = proofs.reduce<Record<string, OrderProof[]>>((acc, p) => {
    (acc[p.type] ??= []).push(p);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      {Object.entries(groups).map(([type, items]) => (
        <div key={type}>
          <p className="mb-1.5 text-xs text-slate-500 dark:text-slate-400">
            {labels[type] ?? type}({items.length})
          </p>
          <div className="flex flex-wrap gap-2">
            {items.map((p) => (
              <ProofThumb
                key={p.storageKey}
                proof={p}
                label={labels[type] ?? type}
                onZoom={() => setZoom(p.storageKey)}
              />
            ))}
          </div>
        </div>
      ))}

      {zoom && <Lightbox storageKey={zoom} onClose={() => setZoom(null)} />}
    </div>
  );
}

function ProofThumb({
  proof,
  label,
  onZoom,
}: {
  proof: OrderProof;
  label: string;
  onZoom: () => void;
}) {
  const url = useProofUrl(proof.thumbnailKey ?? proof.storageKey);

  return (
    <button
      onClick={onZoom}
      className="relative w-24 h-24 rounded-xl overflow-hidden border border-slate-200
                 dark:border-slate-700 bg-slate-100 dark:bg-slate-900 group"
      title={proof.takenAt ? new Date(proof.takenAt).toLocaleString('zh-CN') : undefined}
    >
      {url ? (
        // ★ alt 不能留空。空 alt 等于声明「这是装饰图」,读屏会整个跳过 ——
        //   凭证照片恰恰是这一页最实质的内容
        <img
          src={url}
          alt={`${label}${proof.takenAt ? ` · ${new Date(proof.takenAt).toLocaleString('zh-CN')}` : ''}`}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      ) : (
        <span className="absolute inset-0 grid place-items-center text-slate-400 text-xs">…</span>
      )}
      {/* 有 GPS 的照片标一下 —— 争议时「有没有位置」是关键 */}
      {proof.lat != null && (
        <span className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[10px] py-0.5">
          <InlineIcon icon={MapPin} /> 有定位
        </span>
      )}
    </button>
  );
}

function Lightbox({ storageKey, onClose }: { storageKey: string; onClose: () => void }) {
  const url = useProofUrl(storageKey);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] grid place-items-center bg-black/80 p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="查看凭证照片"
    >
      {url ? (
        <img
          src={url}
          alt="凭证照片(放大)"
          className="max-w-full max-h-full object-contain rounded-lg"
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <span className="text-white">加载中…</span>
      )}
    </div>
  );
}

/** 换一次预签名 URL。组件卸载后不再 setState,避免抽屉关掉后报警告 */
function useProofUrl(key: string): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void api<{ url: string }>(`/uploads/url?key=${encodeURIComponent(key)}`)
      .then((r) => alive && setUrl(r.url))
      .catch((e: unknown) => {
        // 拿不到图不该让整个详情页崩掉,降级成占位符。
        // 但**要留下痕迹** —— 完全吞掉的话,「图不显示」这种问题只能靠猜
        console.warn(
          '凭证图取 URL 失败',
          key,
          e instanceof ApiFailure ? `${e.code}: ${e.message}` : e,
        );
      });
    return () => {
      alive = false;
    };
  }, [key]);

  return url;
}
